import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { withTranslation, useTranslation } from 'react-i18next';
import './Resume.css';

/* ── Colores por paso · paleta Steel Blue ──
   Cada paso usa un tono de la paleta de marca en vez de colores arbitrarios.
   La progresión va de info (telemetría) → steel (datos) → accent (resultado),
   reforzando el flujo conceptual: capturar → analizar → cosechar valor.
*/
const STEP_COLORS = [
  '#2f8a8a', // info — sensores / telemetría
  '#5e7d99', // steel-500 — algoritmos / análisis
  '#4f9d6f', // accent — herramientas / acción
  '#3d7d58', // accent-hover — fallback (4to+ paso)
];

/* ── Flecha entre cards ── */
const FlowArrow = ({ visible, idx }) => (
  <div
    className="xAgro-resume-arrow"
    data-visible={visible}
    style={{ transitionDelay: `${idx * 120 + 350}ms` }}
  >
    <svg width="32" height="20" viewBox="0 0 32 20" fill="none">
      <path
        d="M2 10h24M20 4l6 6-6 6"
        stroke="var(--xagro-accent, #4f9d6f)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.55"
      />
    </svg>
  </div>
);

/* ── Card individual ── */
const StepCard = ({ item, idx, t, visible }) => {
  const color = STEP_COLORS[idx] || STEP_COLORS[STEP_COLORS.length - 1];
  const stepNum = String(idx + 1).padStart(2, '0');

  return (
    <div
      className="xAgro-resume-card"
      data-visible={visible}
      style={{ transitionDelay: `${idx * 120 + 200}ms` }}
    >
      <div className="xAgro-resume-card-inner">
        {/* Ícono — contraste mejorado:
            fondo opaco al 22% del color de paso + borde al 55%
            (antes 8% y 12% → casi invisible sobre el bg-dark) */}
        <div
          className="xAgro-resume-card-icon"
          style={{
            background: `${color}38`,
            borderColor: `${color}8C`,
          }}
        >
          <div
            className="xAgro-resume-card-svg"
            style={{
              background: `url('${item.svg}') center no-repeat`,
              backgroundSize: 'contain',
            }}
          />
        </div>

        {/* Label de paso */}
        <div
          className="xAgro-resume-step-label"
          style={{ color }}
        >
          {stepNum}
        </div>

        {/* Título */}
        <h3 className="xAgro-resume-card-title">
          {t(`${item.title}`)}
        </h3>

        {/* Descripción */}
        <p className="xAgro-resume-card-text">
          {t(`${item.text}`)}
        </p>
      </div>
    </div>
  );
};

/* ── Componente principal ── */
const ResumeSection = function Comp({ data }) {
  const [t] = useTranslation('common');
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  const items = t(`${data}.data`, { returnObjects: true });

  let navigate = useHistory();
  const goToContact = () => {
    const el = document.getElementById('contact');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.hash = '#contact';
    }
  };

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="xAgroResume"
      className="xAgro-resume-section"
      ref={sectionRef}
    >
      {/* Patrón de fondo */}
      <div className="xAgro-resume-bg-pattern" aria-hidden="true" />

      <div className="xAgro-resume-wrapper">

        {/* ── Header ── */}
        <div
          className="xAgro-resume-header"
          data-visible={visible}
        >
          {/* Badge */}
          <div className="xAgro-resume-badge">
            <span className="xAgro-resume-badge-dot" />
            {t(`${data}.badge`, { defaultValue: t(`${data}.subtitle`) })}
          </div>

          {/* Título */}
          <h1 className="xAgro-resume-title">
            {t(`${data}.title`)}
          </h1>

          {/* Subtítulo */}
          <p className="xAgro-resume-subtitle">
            {t(`${data}.text`)}
          </p>
        </div>

        {/* ── Pipeline: Cards + Flechas ── */}
        <div className="xAgro-resume-pipeline">
          {Array.isArray(items) && items.map((item, idx) => (
            <React.Fragment key={`step_${idx}`}>
              <StepCard
                item={item}
                idx={idx}
                t={t}
                visible={visible}
              />
              {idx < items.length - 1 && (
                <FlowArrow visible={visible} idx={idx} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* ── CTA ── */}
        <div
          className="xAgro-resume-cta-wrapper"
          data-visible={visible}
        >
          <button
            className="xAgro-resume-cta"
            onClick={goToContact}
          >
            {t(`${data}.cta`, { defaultValue: t(`${data}.button`) })}
            <svg
              width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5"
              strokeLinecap="round" strokeLinejoin="round"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>

      </div>
    </section>
  );
};

export default withTranslation('common')(ResumeSection);